import React, { useState, useEffect, useRef, lazy, Suspense } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import Header from "../partials/Header";
import Sidebar from "../partials/Sidebar";
import { useSidebar } from "../context/SidebarContext";
import { useToki } from "../context/TokiContext";
import { useSuperApp } from "../hooks/useSuperApp";
import { usePostAction } from "../hooks/usePostAction";
import {
  FiGrid,
  FiX,
  FiUsers,
  FiBriefcase,
  FiPlus,
  FiMapPin,
} from "react-icons/fi";
import { useIsDiilAdmin } from "../hooks/useIsDiilAdmin";
import { preloadJobsByLocation } from "../routes/lazyRoutes";
import { useRegion } from "../context/RegionContext";
import { SearchFilterProvider } from "../context/SearchFilterContext";
import { isDeskPage, DESK_BACKGROUND } from "../utils/pageSurface";
import { PARTTIME_POPUP_PREVIEW } from "../data/parttimeApp";

const LoginModal = lazy(() => import("../components/LoginModal"));
const CompleteProfileModal = lazy(() => import("../components/CompleteProfileModal"));
const ParttimeAppPopup = lazy(() => import("../components/ParttimeAppPopup"));

const Layout = () => {
  const { sidebar, setSidebar } = useSidebar();
  const { profileData } = useToki();
  const { isSuperAppUser, isAuthenticated, isAuthLoading: isLoading, currentUser, retryAuth, nativeApp } = useSuperApp();
  const { goToPost, roleChoiceModal } = usePostAction();
  // Same flag as the sidebar's CV сан row: an admin in their admin profile, not one
  // browsing as a jobseeker.
  const { inAdminMode } = useIsDiilAdmin();
  const region = useRegion();
  const location = useLocation();
  const navigate = useNavigate();
  // Read before the first paint, not in an effect: starting at `false` on a phone drew the page
  // once without the bottom nav and without main's padding, then shifted everything by 88px.
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth < 1024
  );
  
  const [isScrolled, setIsScrolled] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showCompleteProfileModal, setShowCompleteProfileModal] = useState(false);
  // True while any LoginModal (opened from any page) is up — hides the bottom nav
  const [anyLoginModalOpen, setAnyLoginModalOpen] = useState(false);

  useEffect(() => {
    const handler = (e) => setAnyLoginModalOpen(!!e.detail?.open);
    window.addEventListener("diil:loginModal", handler);
    return () => window.removeEventListener("diil:loginModal", handler);
  }, []);
  useEffect(() => {
    // CompleteProfileModal uses E-Mongolia and MN-specific fields - only show for MN region
    if (region.code !== 'MN') {
      setShowCompleteProfileModal(false);
      return;
    }
    // Super app users (Toki/Hambi) already have profile info from the super app - skip
    if (isSuperAppUser) {
      setShowCompleteProfileModal(false);
      return;
    }
    
    if (isAuthenticated && currentUser) {
      const hasName = currentUser.firstName || currentUser.lastName || profileData?.firstName || profileData?.lastName;
      if (!hasName) {
        setShowCompleteProfileModal(true);
      } else {
        setShowCompleteProfileModal(false);
      }
    } else {
      setShowCompleteProfileModal(false);
    }
  }, [isAuthenticated, currentUser, profileData, isSuperAppUser, region.code]);

  // Check if user is trying to access /user/* routes without being logged in
  const isUserRoute = location.pathname.startsWith("/user");
  const superAppRetryAttemptedRef = useRef(false);

  useEffect(() => {
    if (!isLoading && isUserRoute && !isAuthenticated) {
      // For super app users, silently re-auth (only once to prevent infinite loop)
      if (isSuperAppUser) {
        if (!superAppRetryAttemptedRef.current) {
          superAppRetryAttemptedRef.current = true;
          retryAuth();
        }
      } else {
        setShowLoginModal(true);
      }
    } else {
      setShowLoginModal(false);
      // Reset retry flag when user becomes authenticated
      if (isAuthenticated) {
        superAppRetryAttemptedRef.current = false;
      }
    }
  }, [isUserRoute, isAuthenticated, isLoading, isSuperAppUser, retryAuth]);

  const handleLoginSuccess = () => {
    setShowLoginModal(false);
  };

  const handleLoginClose = () => {
    setShowLoginModal(false);
    navigate("/");
  };

  // Track screen size
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024); // phone/tablet — matches tailwind's bumped `md` breakpoint
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Track scroll position
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Determine if header should be hidden or not fixed
  const isProfilePage = location.pathname === "/user/profile";
  // Someone else's public profile (CandidateProfile.jsx) — same self-contained card
  // layout as our own profile, so it hides the topbar the same way.
  const isCandidateProfilePage = location.pathname.startsWith("/candidate/");
  // Profile has its own back button and controls in its hero section (see
  // Profile.jsx) — the global topbar/search bar has no role there, same as chat.
  const hideHeader = isMobile && (location.pathname.startsWith("/chat") || isProfilePage || isCandidateProfilePage);
  const isChatPage = location.pathname.startsWith("/chat");
  const isResumePage = location.pathname === "/user/resume";
  const isTestTakePage = location.pathname.includes("/tests/") && location.pathname.includes("/take");
  const isBookingPage = location.pathname.startsWith("/booking");
  const isPrescreenPage = location.pathname.startsWith("/prescreen") || location.pathname.startsWith("/employer");
  // A job ad brings its own fixed action bar (heart + apply, JobDetail.jsx). The bottom nav
  // under it showed through as just the raised "Зар нийтлэх" circle poking over that bar.
  const isJobDetailPage = location.pathname.startsWith("/job/");
  // The Цагийн ажил app popup (ParttimeAppPopup) — on the browsing side of the site only:
  // not over its own guide page, a chat, a test, an employer or staff screen, nor on top
  // of a login or the complete-profile prompt. It shows once per page load by itself.
  // Phones only — it sells a phone app; on a desktop there is nothing to install it on.
  const showParttimePopup =
    isMobile &&
    (!inAdminMode || PARTTIME_POPUP_PREVIEW) &&
    !showCompleteProfileModal &&
    !location.pathname.startsWith("/parttime-app") &&
    !location.pathname.startsWith("/admin") &&
    !isChatPage && !isTestTakePage && !isBookingPage && !isPrescreenPage;

  // Bottom-nav taps: let the current page visibly settle at the top before
  // the next one mounts, instead of the next page rendering mid-scroll from
  // wherever the previous page was left (mirrors Home's navigateToJob).
  const NAV_SCROLL_TOP_MS = 300;
  const handleNavigate = (path) => {
    if (isMobile && window.scrollY > 0) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      window.setTimeout(() => navigate(path), NAV_SCROLL_TOP_MS);
    } else {
      navigate(path);
    }
  };

  const handleCloseWebview = () => {
    try {
      window.close();
    } catch (e) {
      // Fallback: go back in history
      if (window.history.length > 1) {
        window.history.back();
      }
    }
  };

  return (
    <SearchFilterProvider>
      {/* Fixed top spacer for Toki app on mobile - never scrolls away */}
      {nativeApp === 'toki' && isMobile && (
        <div className="fixed top-0 left-0 right-0 h-[40px] bg-white z-50" />
      )}
      {/* Close webview button for Hambi app */}
      {nativeApp === 'hambi' && (
        <button
          onClick={handleCloseWebview}
          className="fixed top-3 right-3 z-[60] w-8 h-8 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center shadow-lg active:scale-95 transition-transform"
        >
          <FiX className="text-white text-lg" />
        </button>
      )}
      {/* Only the x axis is clipped (overflow-x: clip leaves y visible, unlike hidden):
          on the map page the content is `fixed` and takes no height, so this row is just
          the header tall and a vertical clip cut the profile menu off a few rows down. */}
      <div className={`flex w-full overflow-x-clip ${nativeApp === 'toki' && isMobile ? "pt-[40px]" : ""}`}>
        {/* Desktop Sidebar */}
        <aside
          // 300ms everywhere the collapse touches (aside, inner panel, content column, header),
          // otherwise the parts arrive at different times and leave a gutter mid-animation
          className={`hidden lg:block fixed left-0 top-0 h-screen transition-all duration-300 z-40 ${sidebar ? "w-[14.75rem]" : "w-[5rem]"
            }`}
        >
          <Sidebar />
        </aside>

        {/* Mobile Sidebar */}
        <aside
          className={`lg:hidden fixed top-0 h-screen transition-transform duration-100 z-50
          ${sidebar ? "translate-x-0" : "translate-x-full"}
          ${isMobile ? "w-full" : "w-[14.75rem]"}
          right-0
        `}
        >
          <Sidebar />
        </aside>

        {/* Bottom Mobile Menu - Glassmorphism Design */}
        {isMobile && !isChatPage && !isResumePage && !isTestTakePage && !isBookingPage && !isPrescreenPage && !isJobDetailPage && !showLoginModal && !anyLoginModalOpen && (
          <div
            className="lg:hidden fixed left-4 right-4 z-50"
            // index.html asks for viewport-fit=cover, so the page reaches into the home-indicator
            // strip; without the inset the bar's lower edge sits under it and fights the swipe.
            style={{ bottom: `calc(env(safe-area-inset-bottom, 0px) + ${nativeApp === 'toki' ? '2rem' : '1rem'})` }}
          >
            <div
              className="rounded-[26px] py-2.5 px-2 shadow-lg"
              style={{
                background: 'var(--nav-glass-bg, rgba(255, 255, 255, 0.72))',
                backdropFilter: 'blur(24px) saturate(200%)',
                WebkitBackdropFilter: 'blur(24px) saturate(200%)',
                border: '1px solid var(--nav-glass-border, rgba(255, 255, 255, 0.5))',
                boxShadow: 'var(--nav-glass-shadow, 0 8px 32px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(255, 255, 255, 0.2) inset)'
              }}
            >
              {/* items-end (not items-center): the raised circle is taller than a plain
                  icon, so centering the row would float every label at a different height.
                  Aligning to the bottom keeps every label on the same baseline. */}
              <div className="flex items-end gap-1">
                {[
                  { key: "home", label: "Нүүр", Icon: FiGrid, active: location.pathname === "/", onClick: () => handleNavigate("/") },
                  // Байршлууд has no bottom-nav slot: the map (/location) is reached
                  // from the round map button on the home banner (BannerCarousel.jsx).
                  { key: "jobs", label: "Ажлын байр", Icon: FiBriefcase, active: location.pathname.startsWith("/jobs"), onClick: () => handleNavigate("/jobs") },
                  // Centre "post" button — routes by account role (employer vs
                  // job seeker), not by which page the user is currently on.
                  { key: "post", label: "Зар нийтлэх", Icon: FiPlus, active: false, onClick: () => goToPost(() => setShowLoginModal(true)) },
                  // The map, for everyone. This slot used to be Цагийн ажил (the separate
                  // part-time app), which now announces itself in ParttimeAppPopup; an
                  // admin's CV сан, which also sat here, is the "CV сан" tab on /search.
                  { key: "map", label: "Газрын зураг", Icon: FiMapPin, active: location.pathname.startsWith("/location"), onClick: () => { preloadJobsByLocation(); handleNavigate("/location"); } },
                  { key: "group", label: "Ажлын Групп", Icon: FiUsers, active: location.pathname.startsWith("/job-group"), onClick: () => handleNavigate("/job-group") },
                ].map(({ key, label, Icon, active, onClick }) => (
                  <button
                    key={key}
                    onClick={onClick}
                    // min-w-0 makes the flex-1 columns actually divide the bar evenly instead
                    // of auto-widening to fit each label's content, which is what let
                    // "Ажлын байр" and "Зар нийтлэх" run into each other in adjacent columns.
                    // post gets extra flex-grow so its longer label has more room without
                    // taking a fixed width away from the others at every screen size. It's
                    // also shifted up as a whole block (-translate-y) so its label lands at
                    // the other buttons' icon height instead of their label baseline.
                    className={`${key === "post" ? "flex-[1.3] -translate-y-1" : "flex-1"} min-w-0 flex flex-col items-center justify-end gap-1 py-0.5 px-0.5 bg-transparent`}
                  >
                    {key === "group" ? (
                      <span className="relative flex items-center justify-center">
                        <Icon className={`text-[22px] transition-colors ${active ? "text-[#020618]" : "text-[#94A3B8]"}`} />
                        {!active && <span className="absolute -top-2 -right-2 text-[13px] leading-none animate-bounce select-none">🤩</span>}
                      </span>
                    ) : key === "post" ? (
                      // Raised circular FAB, like a QR-payment button: pulled up out of the
                      // bar with a ring matching the bar's own background, so it reads as
                      // its own floating badge that's still nested into the nav rather than
                      // a bar item like the rest.
                      <span
                        className="relative -mt-9 flex items-center justify-center w-12 h-12 rounded-full"
                        style={{
                          background: "#020618",
                          boxShadow: "0 10px 22px -6px rgba(2,6,24,0.5), 0 0 0 5px var(--nav-glass-bg, rgba(255,255,255,0.72))",
                        }}
                      >
                        <Icon className="text-[22px]" style={{ color: "#FFFFFF" }} strokeWidth={2.5} />
                      </span>
                    ) : (
                      <Icon className={`text-[22px] transition-colors ${active ? "text-[#020618]" : "text-[#94A3B8]"}`} />
                    )}
                    {/* Every label stays on a single line — wrapping made the taller
                        columns push their icons out of line with the rest. The type is
                        sized down instead so the longest label ("Ажлын Байр")
                        still fits its column. */}
                    <span
                      className={`text-[9px] leading-tight whitespace-nowrap text-center transition-colors ${active ? "text-[#020618] font-extrabold" : key === "post" ? "text-[#020618] font-extrabold" : "text-[#94A3B8] font-bold"
                        }`}
                    >
                      {label}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Overlay for mobile/tablet */}
        {sidebar && (
          <div
            className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={() => setSidebar(false)}
          />
        )}

        {/* Main content.
            The width has to be the viewport minus the sidebar, not `w-full`: the sidebar is
            `fixed`, so it takes no space in this flex row, and a full-width column shifted right
            by its margin hangs off the screen — the parent's overflow-hidden then cut the last
            236px (the login button and the whole filter column) at every width below ~1500px.
            Same calc the sticky header below already uses. */}
        <div
          className={`flex-1 w-full min-w-0 transition-all duration-300 ${sidebar
            ? "lg:flex-none lg:w-[calc(100%-14.75rem)] lg:ml-[14.75rem]"
            : "lg:flex-none lg:w-[calc(100%-5rem)] lg:ml-[5rem]"
            }`}
        >
          {/* Header — hidden on chat pages (mobile) and test take pages */}
          {!hideHeader && !isTestTakePage && (
            <div
              className={`w-full transition-all duration-300 ${isScrolled && !isChatPage
                ? `fixed ${nativeApp === 'toki' && isMobile ? "top-[40px] bg-white" : "top-0"} z-30 md:border-b border-[#CAD5E2] ${sidebar
                  ? "lg:w-[calc(100%-14.75rem)]"
                  : "lg:w-[calc(100%-5rem)]"
                }`
                : "relative w-full"
                }`}
            >
              <Header />
            </div>
          )}

          {/* The desk tint sits here as well as on the page itself, because `main` is wider
              than what the page paints: on a phone it adds 5.5rem of padding under the
              content to clear the bottom nav, and that strip was left showing the body's
              white below a tinted page. See utils/pageSurface. */}
          <main
            style={isDeskPage(location.pathname) && isMobile ? { background: DESK_BACKGROUND } : undefined}
            className={`
    ${isScrolled && !isChatPage && !isTestTakePage ? "pt-[var(--header-height,4rem)]" : ""}
        ${isMobile && !isChatPage && !isResumePage && !isTestTakePage && !isBookingPage && !isPrescreenPage && !isJobDetailPage ? "pb-[calc(5.5rem+env(safe-area-inset-bottom,0px))]" : ""}
  `}
          >
            <Outlet />
          </main>
        </div>
      </div>

      {/* Phone Login Modal for /user/* routes - never shown for super app users */}
      {!isSuperAppUser && showLoginModal && (
        <Suspense fallback={null}>
          <LoginModal
            isOpen={showLoginModal}
            trigger={isUserRoute ? "user_route" : "post_job"}
            onClose={() => setShowLoginModal(false)}
            onSuccess={handleLoginSuccess}
          />
        </Suspense>
      )}
      {showCompleteProfileModal && (
        <Suspense fallback={null}>
          <CompleteProfileModal
            isOpen={showCompleteProfileModal}
            onClose={() => setShowCompleteProfileModal(false)}
          />
        </Suspense>
      )}
      {showParttimePopup && !showLoginModal && !anyLoginModalOpen && (
        <Suspense fallback={null}>
          <ParttimeAppPopup />
        </Suspense>
      )}
      
      {roleChoiceModal}
    </SearchFilterProvider>
  );
};

export default Layout;
